import type { AgentStep, GenerateResponse, GlossaryTerm, QueryResponse, Suggestion, Visualization } from "@unbox/api";
import { Fragment, memo, useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { useGlossaryTerms, useSubmitAskFeedback } from "@/api/ask";
import { boldNumbers, findingLabel } from "@/pages/ask/lib/answer-text";
import { useAskSurface } from "@/pages/ask/lib/ask-surface";
import type { Scope } from "@/pages/ask/lib/data";
import { categorizeError } from "@/pages/ask/lib/error-display";
import { showQueryInternals } from "@/pages/ask/lib/flags";
import type { StreamingPhase } from "@/pages/ask/lib/stream-status";
import { collectLinkEntities, findLinkMatches, LINK_STYLE, type LinkEntities } from "@/pages/ask/lib/tiktok-links";
import { toCsv } from "../lib/csv";
import { AnswerStatus, useAnswerStatus } from "./AnswerStatus";
import Visualizations from "./Charts";
import { DisplaySkeleton } from "./DisplaySkeleton";
import { InlineErrorCard, SourceLabels } from "./DrawerAnswerParts";
import Icon from "./Icon";
import ReportViewer from "./ReportViewer";
import ResultsTable from "./ResultsTable";
import { UnboxMark } from "./UnboxMark";

// ---------------------------------------------------------------------------
// Definition tooltip — dotted underline + hover card for glossary terms
// ---------------------------------------------------------------------------

function DefinitionTooltip({ children, term }: { children: React.ReactNode; term: GlossaryTerm }) {
  const [visible, setVisible] = useState(false);
  const tooltipId = useId();

  const FAMILY_ACCENTS: Record<string, string> = {
    Creator: "var(--color-success-600)",
    Content: "var(--color-warning-600)",
    Safety: "var(--color-error-600)",
    Reporting: "var(--color-brand-600)",
    Adoption: "var(--color-gray-500)",
  };
  const accent = FAMILY_ACCENTS[term.family] ?? "var(--color-brand-500)";

  return (
    <span style={{ position: "relative", display: "inline" }}>
      {/* Trigger: keyboard-focusable, shows tooltip on focus and hover */}
      <span
        role="button"
        tabIndex={0}
        aria-describedby={visible ? tooltipId : undefined}
        onMouseEnter={() => setVisible(true)}
        onMouseLeave={() => setVisible(false)}
        onFocus={() => setVisible(true)}
        onBlur={() => setVisible(false)}
        style={{
          textDecoration: "underline",
          textDecorationStyle: "dotted",
          textDecorationColor: accent,
          textUnderlineOffset: "3px",
          cursor: "help",
        }}
      >
        {children}
      </span>

      {visible && (
        <span
          id={tooltipId}
          role="tooltip"
          style={{
            position: "absolute",
            bottom: "calc(100% + 8px)",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 200,
            width: 260,
            background: "var(--ask-popover-bg, var(--color-bg-primary))",
            border: "1px solid var(--ask-popover-border, var(--color-border-secondary))",
            borderRadius: 8,
            boxShadow: "var(--ask-popover-shadow, 0 4px 16px rgba(0,0,0,0.12))",
            padding: "10px 12px",
            pointerEvents: "none",
            display: "block",
          }}
        >
          {/* Term name + family badge */}
          <span style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: "var(--color-text-primary)", lineHeight: "18px" }}>
              {term.term}
            </span>
            <span
              style={{
                fontSize: 10,
                fontWeight: 500,
                padding: "1px 6px",
                borderRadius: 99,
                background: `color-mix(in srgb, ${accent} 12%, white)`,
                color: accent,
                border: `1px solid color-mix(in srgb, ${accent} 25%, white)`,
                lineHeight: "16px",
                flexShrink: 0,
              }}
            >
              {term.family}
            </span>
          </span>

          {/* Definition */}
          <span style={{ fontSize: 12, lineHeight: "18px", color: "var(--color-text-secondary)", display: "block" }}>
            {term.definition}
          </span>

          {/* Default window */}
          {term.defaultWindow && (
            <span
              style={{
                display: "block",
                marginTop: 7,
                fontSize: 11,
                color: "var(--color-text-quaternary)",
                fontFamily: "var(--font-mono)",
              }}
            >
              Default window: {term.defaultWindow}
            </span>
          )}
        </span>
      )}
    </span>
  );
}

// ---------------------------------------------------------------------------
// annotateText — split a plain text string into React nodes, wrapping any
// glossary terms in DefinitionTooltip. Only terms present in termMap are
// annotated (caller should pre-filter to definitions_applied).
// ---------------------------------------------------------------------------

function escapeRegex(s: string) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function annotateText(text: string, termMap: Map<string, GlossaryTerm>): React.ReactNode[] {
  if (!termMap || termMap.size === 0) return [text];

  // Sort keys longest-first so "Active Creator" matches before "Creator"
  const keys = [...termMap.keys()].sort((a, b) => b.length - a.length);
  const pattern = new RegExp(`\\b(${keys.map(escapeRegex).join("|")})\\b`, "gi");

  const nodes: React.ReactNode[] = [];
  let lastIndex = 0;

  // Reset lastIndex — regex is stateful when using 'g'
  pattern.lastIndex = 0;

  for (let match = pattern.exec(text); match !== null; match = pattern.exec(text)) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }
    const term = termMap.get(match[1].toLowerCase());
    if (term) {
      nodes.push(
        <DefinitionTooltip key={`def-${match.index}`} term={term}>
          {match[1]}
        </DefinitionTooltip>,
      );
    } else {
      nodes.push(match[1]);
    }
    lastIndex = match.index + match[1].length;
  }

  if (lastIndex < text.length) nodes.push(text.slice(lastIndex));
  return nodes;
}

// annotateLinks — wrap data-backed creator handles / video ids as TikTok links,
// then run the existing glossary annotation on the remaining plain text.
function annotateLinks(
  text: string,
  entities: LinkEntities | undefined,
  termMap: Map<string, GlossaryTerm>,
): React.ReactNode[] {
  if (!entities || (entities.creators.size === 0 && entities.videos.size === 0)) {
    return annotateText(text, termMap);
  }
  const nodes: React.ReactNode[] = [];
  findLinkMatches(text, entities).forEach((seg, i) => {
    if (seg.kind === "plain") {
      nodes.push(<Fragment key={`pl-${i}`}>{annotateText(seg.text, termMap)}</Fragment>);
    } else if (seg.kind === "creator") {
      nodes.push(
        <a key={`lk-${i}`} href={seg.url} target="_blank" rel="noopener noreferrer" style={LINK_STYLE}>
          {seg.text}
        </a>,
      );
    } else {
      nodes.push(
        <a
          key={`lk-${i}`}
          href={seg.url}
          target="_blank"
          rel="noopener noreferrer"
          style={{ ...LINK_STYLE, fontFamily: "var(--font-mono)", fontSize: "0.9em" }}
        >
          {seg.text}
        </a>,
      );
    }
  });
  return nodes;
}

// ---------------------------------------------------------------------------
// Lightweight markdown renderer — handles **bold**, `code`, and - bullets.
// Covers everything Claude writes in explanations and notes without needing
// an external library.
// ---------------------------------------------------------------------------

function InlineMarkdown({
  text,
  termMap,
  entities,
}: {
  text: string;
  termMap?: Map<string, GlossaryTerm>;
  entities?: LinkEntities;
}) {
  const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`)/);
  return (
    <>
      {parts.map((part, i) => {
        if (part.startsWith("**") && part.endsWith("**")) {
          const inner = part.slice(2, -2);
          return <strong key={i}>{annotateLinks(inner, entities, termMap ?? new Map())}</strong>;
        }
        if (part.startsWith("`") && part.endsWith("`")) {
          // Never annotate inside code spans
          return (
            <code
              key={i}
              style={{
                background: "var(--color-gray-100)",
                padding: "1px 5px",
                borderRadius: 3,
                fontSize: "0.88em",
                fontFamily: "var(--font-mono)",
                color: "var(--color-text-secondary)",
              }}
            >
              {part.slice(1, -1)}
            </code>
          );
        }
        return <span key={i}>{annotateLinks(part, entities, termMap ?? new Map())}</span>;
      })}
    </>
  );
}

function Markdown({
  text,
  style,
  termMap,
  entities,
}: {
  text: string;
  style?: React.CSSProperties;
  termMap?: Map<string, GlossaryTerm>;
  entities?: LinkEntities;
}) {
  const lines = text.split("\n");
  const elements: React.ReactNode[] = [];
  let bulletBuffer: string[] = [];
  let paraBuffer: string[] = [];
  let tableBuffer: string[] = [];

  function flushBullets() {
    if (bulletBuffer.length === 0) return;
    elements.push(
      <ul key={`ul-${elements.length}`} style={{ margin: "4px 0 8px", paddingLeft: 20, listStyle: "disc" }}>
        {bulletBuffer.map((b, i) => (
          <li key={i} style={{ marginBottom: 3, lineHeight: "22px" }}>
            <InlineMarkdown text={b} termMap={termMap} entities={entities} />
          </li>
        ))}
      </ul>,
    );
    bulletBuffer = [];
  }

  function flushPara() {
    if (paraBuffer.length === 0) return;
    const joined = paraBuffer.join(" ");
    elements.push(
      <p key={`p-${elements.length}`} style={{ margin: "0 0 8px" }}>
        <InlineMarkdown text={joined} termMap={termMap} entities={entities} />
      </p>,
    );
    paraBuffer = [];
  }

  // Parse "| a | b |" into trimmed cells, tolerating optional leading/trailing pipes
  function parseRow(row: string): string[] {
    let r = row.trim();
    if (r.startsWith("|")) r = r.slice(1);
    if (r.endsWith("|")) r = r.slice(0, -1);
    return r.split("|").map((c) => c.trim());
  }

  function flushTable() {
    if (tableBuffer.length === 0) return;
    const rows = tableBuffer;
    tableBuffer = [];

    const isSeparator = (row: string) => {
      const cells = parseRow(row);
      return cells.length > 0 && cells.every((c) => /^:?-{1,}:?$/.test(c.replace(/\s/g, "")));
    };

    // A valid GFM table needs a header row followed by a separator row.
    if (rows.length < 2 || !isSeparator(rows[1])) {
      elements.push(
        <p key={`p-${elements.length}`} style={{ margin: "0 0 8px" }}>
          {rows.map((r, i) => (
            <span key={i}>
              {i > 0 ? <br /> : null}
              <InlineMarkdown text={r} termMap={termMap} entities={entities} />
            </span>
          ))}
        </p>,
      );
      return;
    }

    const header = parseRow(rows[0]);
    const aligns = parseRow(rows[1]).map((c) => {
      const s = c.replace(/\s/g, "");
      const left = s.startsWith(":");
      const right = s.endsWith(":");
      return left && right ? "center" : right ? "right" : "left";
    });
    const body = rows.slice(2).map(parseRow);
    const alignOf = (i: number) => (aligns[i] ?? "left") as React.CSSProperties["textAlign"];

    elements.push(
      <div key={`tbl-${elements.length}`} style={{ overflowX: "auto", margin: "6px 0 12px" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, lineHeight: "20px" }}>
          <thead>
            <tr>
              {header.map((h, i) => (
                <th
                  key={i}
                  style={{
                    textAlign: alignOf(i),
                    padding: "6px 12px",
                    borderBottom: "1px solid var(--color-border-secondary)",
                    fontWeight: 600,
                    fontSize: 12,
                    color: "var(--color-text-secondary)",
                    whiteSpace: "nowrap",
                  }}
                >
                  <InlineMarkdown text={h} termMap={termMap} entities={entities} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {body.map((cells, ri) => (
              <tr key={ri}>
                {header.map((_, ci) => (
                  <td
                    key={ci}
                    style={{
                      textAlign: alignOf(ci),
                      padding: "6px 12px",
                      borderBottom: "1px solid var(--color-border-tertiary)",
                      color: "var(--color-text-secondary)",
                      verticalAlign: "top",
                    }}
                  >
                    <InlineMarkdown text={cells[ci] ?? ""} termMap={termMap} entities={entities} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>,
    );
  }

  for (const line of lines) {
    const trimmed = line.trim();
    const bulletMatch = line.match(/^[-*]\s+(.+)/);
    if (trimmed.startsWith("|")) {
      flushBullets();
      flushPara();
      tableBuffer.push(trimmed);
    } else if (bulletMatch) {
      flushTable();
      flushPara();
      bulletBuffer.push(bulletMatch[1]);
    } else if (trimmed === "") {
      flushTable();
      flushBullets();
      flushPara();
    } else {
      flushTable();
      flushBullets();
      paraBuffer.push(trimmed);
    }
  }
  flushTable();
  flushBullets();
  flushPara();

  return <div style={style}>{elements}</div>;
}

// ---------------------------------------------------------------------------
// SectionLabel — uppercase eyebrow + trailing hairline divider
// ---------------------------------------------------------------------------
function SectionLabel({ label }: { label: string }) {
  if (useAskSurface() === "drawer") return <div className="ask-card-label ask-card-label--quiet">{label}</div>;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
      <span
        style={{
          fontSize: 11,
          fontWeight: 700,
          color: "var(--color-text-tertiary)",
          textTransform: "uppercase",
          letterSpacing: "0.1em",
          flexShrink: 0,
        }}
      >
        {label}
      </span>
      <div style={{ height: 1, flex: 1, background: "var(--color-border-secondary)" }} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// SummaryCard / FindingsCard — the drawer's card treatment of the answer and findings
// ---------------------------------------------------------------------------
function SummaryCard({
  text,
  termMap,
  entities,
}: {
  text: string;
  termMap?: Map<string, GlossaryTerm>;
  entities?: LinkEntities;
}) {
  return (
    <div className="ask-card ask-analysis">
      <div className="ask-card-label">
        <UnboxMark size={14} />
        Summary
      </div>
      <Markdown
        text={boldNumbers(text)}
        termMap={termMap}
        entities={entities}
        style={{ fontSize: 15, lineHeight: "25px", color: "rgb(255 255 255 / 0.62)" }}
      />
    </div>
  );
}

function FindingsCard({
  findings,
  termMap,
  entities,
}: {
  findings: string[];
  termMap?: Map<string, GlossaryTerm>;
  entities?: LinkEntities;
}) {
  return (
    <div className="ask-card ask-findings">
      <div className="ask-card-label">
        Key findings
        <span className="ask-findings-count">{findings.length}</span>
      </div>
      <ol className="ask-findings-list">
        {findings.map((finding, i) => {
          const { label, text } = findingLabel(finding.replace(/^\d+\.\s+/, ""));
          return (
            <li key={i} className="ask-finding">
              <span className="ask-finding-number" aria-hidden="true">
                {String(i + 1).padStart(2, "0")}
              </span>
              <span className="ask-finding-text">
                <span className="ask-finding-eyebrow">{label}</span>
                <InlineMarkdown text={boldNumbers(text)} termMap={termMap} entities={entities} />
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Answer blocks — each renders the drawer's card or the page's flat version
// ---------------------------------------------------------------------------
function AnswerSources({ steps, currentStepLabel }: { steps: AgentStep[]; currentStepLabel?: string }) {
  if (useAskSurface() === "drawer") return <SourceLabels steps={steps} />;
  return <SourcesBlock steps={steps} currentStepLabel={currentStepLabel} />;
}

function AnswerText({
  text,
  termMap,
  entities,
  labelled = true,
  errored = false,
}: {
  text: string;
  termMap?: Map<string, GlossaryTerm>;
  entities?: LinkEntities;
  /** The page heads the prose with "Analysis" only when sources precede it. */
  labelled?: boolean;
  /** The drawer leaves the summary out when the error card explains what happened. */
  errored?: boolean;
}) {
  if (useAskSurface() === "drawer") {
    return errored ? null : <SummaryCard text={text} termMap={termMap} entities={entities} />;
  }
  return (
    <>
      {labelled && <SectionLabel label="Analysis" />}
      <Markdown
        text={text}
        termMap={termMap}
        entities={entities}
        style={{ fontSize: 15, lineHeight: "26px", color: "var(--color-text-primary)", marginBottom: 12 }}
      />
    </>
  );
}

function AnswerFindings({
  findings,
  termMap,
  entities,
}: {
  findings: string[];
  termMap?: Map<string, GlossaryTerm>;
  entities?: LinkEntities;
}) {
  if (useAskSurface() === "drawer") return <FindingsCard findings={findings} termMap={termMap} entities={entities} />;
  return <FindingsBlock findings={findings} termMap={termMap ?? new Map<string, GlossaryTerm>()} entities={entities} />;
}

function AnswerError(props: Parameters<typeof ErrorBlock>[0]) {
  if (useAskSurface() === "drawer") return <InlineErrorCard {...props} />;
  return <ErrorBlock {...props} />;
}

function AssistantAvatar() {
  if (useAskSurface() === "drawer") return null;
  return (
    <div
      style={{
        width: 28,
        height: 28,
        borderRadius: 8,
        background: "var(--color-brand-900)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        marginTop: 2,
      }}
    >
      <svg
        width={14}
        height={14}
        viewBox="0 0 24 24"
        fill="none"
        stroke="#fff"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
        <path d="M18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456z" />
      </svg>
    </div>
  );
}

// ---------------------------------------------------------------------------
// FindingsBlock — numbered finding cards replacing plain bullet list
// ---------------------------------------------------------------------------

function FindingsBlock({
  findings,
  termMap,
  entities,
}: {
  findings: string[];
  termMap: Map<string, GlossaryTerm>;
  entities?: LinkEntities;
}) {
  return (
    <div style={{ marginBottom: 14 }}>
      <SectionLabel label="Key findings" />

      <ol style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 12 }}>
        {findings.map((f, i) => {
          // Strip leading "N." numbering Claude sometimes prefixes
          const text = f.replace(/^\d+\.\s+/, "");
          // Detect "ALL CAPS LABEL: rest" or "ALL CAPS LABEL — rest"
          const labelMatch = text.match(/^([A-Z][A-Z\s/-]{2,})(?::\s*|\s+—\s*)([\s\S]+)/);

          return (
            <li key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
              <span
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: "50%",
                  flexShrink: 0,
                  marginTop: 1,
                  border: "1px solid var(--color-brand-200)",
                  background: "var(--color-brand-50)",
                  color: "var(--color-brand-700)",
                  fontSize: 12,
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {i + 1}
              </span>
              <span
                style={{ flex: 1, minWidth: 0, fontSize: 14, lineHeight: "21px", color: "var(--color-text-secondary)" }}
              >
                {labelMatch ? (
                  <>
                    <strong style={{ color: "var(--color-text-primary)" }}>{labelMatch[1]}:</strong>{" "}
                    <InlineMarkdown text={labelMatch[2]} termMap={termMap} entities={entities} />
                  </>
                ) : (
                  <InlineMarkdown text={text} termMap={termMap} entities={entities} />
                )}
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

export type Message = {
  id: string;
  role: "user" | "assistant";
  content: string;
  scope?: Scope | null;
  queryResponse?: QueryResponse;
  generatedReport?: GenerateResponse;
  agentSteps?: AgentStep[]; // in-progress steps accumulate here
  currentStepLabel?: string; // label of the step currently running
  streamingFindings?: string[]; // findings streamed live before the final result
  streamingPhase?: StreamingPhase; // which part of the answer is being written
  streamingDisplay?: Visualization | null; // the display once chosen; null for a text-only answer
  reportRun?: boolean; // a report template is generating, rather than a question being answered
  stopped?: boolean; // true when the user stopped the query mid-flight
  // passed through from the parent so ResponseActions can log feedback
  question?: string;
  sessionId?: string;
  messageId?: string; // the saved answer, set once the server has stored it
};

// ---------------------------------------------------------------------------
// Feedback categories — shown when thumbs-down is clicked
// ---------------------------------------------------------------------------
const FEEDBACK_CATEGORIES: { id: string; label: string }[] = [
  { id: "wrong_metric", label: "Wrong metric definition" },
  { id: "wrong_window", label: "Wrong time window" },
  { id: "wrong_scope", label: "Wrong brand / scope" },
  { id: "sql_error", label: "SQL failed or bad data" },
  { id: "incomplete", label: "Answer was incomplete" },
  { id: "too_many_steps", label: "Too many / too few queries" },
];

// ---------------------------------------------------------------------------
// REPORT_FOLLOWUPS — per-template follow-up suggestions shown below reports
// ---------------------------------------------------------------------------
const REPORT_FOLLOWUPS: Record<string, Suggestion[]> = {
  "business-review": [
    { kind: "deeper", question: "Which creators contributed most to GMV in this period?" },
    { kind: "broaden", question: "How does affiliate GMV compare to GMV Max attribution over the last 90 days?" },
  ],
  "brand-safety-risk-profile": [
    { kind: "deeper", question: "Which videos were flagged most recently, and what were the flag types?" },
    { kind: "broaden", question: "How has the flag rate trended over the last 90 days?" },
  ],
  "starter-sku-recommendation": [
    { kind: "deeper", question: "Which products have the highest GMV per video?" },
    { kind: "broaden", question: "Which creators perform best with these starter products?" },
  ],
  "sample-efficiency": [
    { kind: "deeper", question: "Which samples converted into GMV-driving videos?" },
    { kind: "broaden", question: "How does sample fulfilment volume compare to video output?" },
  ],
  "creator-success-profile": [
    { kind: "deeper", question: "What do the top-quartile creators have in common?" },
    { kind: "broaden", question: "Which prospective creators match this success profile?" },
  ],
};

// ---------------------------------------------------------------------------
// ResponseActions — export + feedback row shown below completed responses
// ---------------------------------------------------------------------------
function ResponseActions({
  response,
  question,
  sessionId,
  messageId,
  answerTable,
  onExportCsv,
}: {
  response: QueryResponse;
  question?: string;
  sessionId?: string;
  messageId?: string;
  answerTable: { columns: string[]; rows: Record<string, unknown>[] } | null;
  onExportCsv: () => void;
}) {
  const submitFeedback = useSubmitAskFeedback();
  const [rating, setRating] = useState<1 | -1 | null>(null);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [categories, setCategories] = useState<string[]>([]);
  const [correction, setCorrection] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const submitting = submitFeedback.isPending;

  const handleThumbsUp = useCallback(() => {
    if (rating !== null) return;
    setRating(1);
    submitFeedback.mutate({
      session_id: sessionId ?? null,
      message_id: messageId ?? null,
      question: question ?? "",
      rating: 1,
      tables_referenced: response.tables_referenced ?? [],
      step_count: response.steps?.length ?? 0,
    });
  }, [rating, question, sessionId, messageId, response, submitFeedback]);

  const handleThumbsDown = useCallback(() => {
    if (rating === 1) return;
    setRating(-1);
    setFeedbackOpen(true);
  }, [rating]);

  const toggleCategory = (id: string) => {
    setCategories((prev) => (prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]));
  };

  const handleSubmitFeedback = async () => {
    try {
      await submitFeedback.mutateAsync({
        session_id: sessionId ?? null,
        message_id: messageId ?? null,
        question: question ?? "",
        rating: -1,
        categories,
        correction: correction.trim() || null,
        tables_referenced: response.tables_referenced ?? [],
        step_count: response.steps?.length ?? 0,
      });
    } catch {
      // Feedback is best-effort — never block the user on it.
    }
    setSubmitted(true);
    setFeedbackOpen(false);
  };

  const rowCount = answerTable?.rows.length ?? 0;
  const downloadLabel = `Download data (${rowCount.toLocaleString()} row${rowCount === 1 ? "" : "s"}) · CSV`;

  return (
    <div style={{ marginTop: 8 }}>
      {/* Action row */}
      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
        {/* Download CSV — the model-designated answer table */}
        {answerTable && (
          <>
            <button
              type="button"
              onClick={onExportCsv}
              title="Download the answer's data as a CSV file"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                height: 28,
                padding: "0 10px",
                borderRadius: 6,
                border: "1px solid var(--ask-cta-bg)",
                background: "var(--ask-cta-bg)",
                color: "var(--ask-cta-fg)",
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: "inherit",
                transition: "background 120ms, border-color 120ms",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "var(--ask-cta-bg-hover)";
                e.currentTarget.style.borderColor = "var(--ask-cta-bg-hover)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "var(--ask-cta-bg)";
                e.currentTarget.style.borderColor = "var(--ask-cta-bg)";
              }}
            >
              <Icon name="download" size={13} color="currentColor" strokeWidth={2} />
              {downloadLabel}
            </button>
            <div style={{ width: 1, height: 16, background: "var(--color-border-secondary)", margin: "0 4px" }} />
          </>
        )}

        {/* Thumbs up */}
        <button
          onClick={handleThumbsUp}
          title="This answer was helpful"
          style={{
            width: 28,
            height: 28,
            borderRadius: 6,
            border: `1px solid ${rating === 1 ? "var(--color-success-300)" : "transparent"}`,
            background: rating === 1 ? "var(--color-success-50)" : "transparent",
            color: rating === 1 ? "var(--color-success-700)" : "var(--color-text-quaternary)",
            cursor: rating === 1 ? "default" : "pointer",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "all 120ms",
            fontSize: 13,
          }}
        >
          👍
        </button>

        {/* Thumbs down */}
        <button
          onClick={handleThumbsDown}
          title="Something was wrong"
          style={{
            width: 28,
            height: 28,
            borderRadius: 6,
            border: `1px solid ${rating === -1 ? "var(--color-error-300)" : "transparent"}`,
            background: rating === -1 ? "var(--color-error-50)" : "transparent",
            color: rating === -1 ? "var(--color-error-700)" : "var(--color-text-quaternary)",
            cursor: rating === 1 ? "default" : "pointer",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "all 120ms",
            fontSize: 13,
          }}
        >
          👎
        </button>

        {/* Positive confirmation */}
        {rating === 1 && (
          <span style={{ fontSize: 12, color: "var(--color-success-700)", marginLeft: 4 }}>
            Thanks — helpful responses get promoted to query templates.
          </span>
        )}

        {/* Submitted confirmation */}
        {submitted && (
          <span style={{ fontSize: 12, color: "var(--color-text-quaternary)", marginLeft: 4 }}>
            Feedback recorded. Corrections are reviewed to improve metric definitions.
          </span>
        )}
      </div>

      {/* Feedback panel — expands on thumbs down */}
      {feedbackOpen && !submitted && (
        <div
          style={{
            marginTop: 10,
            padding: "12px 14px",
            border: "1px solid var(--color-border-secondary)",
            borderRadius: 8,
            background: "var(--color-gray-50)",
            fontSize: 13,
          }}
        >
          <p style={{ margin: "0 0 10px", fontWeight: 500, color: "var(--color-text-secondary)", fontSize: 12 }}>
            What was wrong? (select all that apply)
          </p>

          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
            {FEEDBACK_CATEGORIES.map((cat) => {
              const selected = categories.includes(cat.id);
              return (
                <button
                  key={cat.id}
                  onClick={() => toggleCategory(cat.id)}
                  style={{
                    height: 26,
                    padding: "0 10px",
                    borderRadius: 99,
                    border: `1px solid ${selected ? "var(--color-brand-400)" : "var(--color-border-secondary)"}`,
                    background: selected ? "var(--color-brand-50)" : "var(--color-bg-primary)",
                    color: selected ? "var(--color-brand-700)" : "var(--color-text-secondary)",
                    fontSize: 12,
                    fontWeight: selected ? 500 : 400,
                    cursor: "pointer",
                    fontFamily: "inherit",
                    transition: "all 80ms",
                  }}
                >
                  {cat.label}
                </button>
              );
            })}
          </div>

          <textarea
            value={correction}
            onChange={(e) => setCorrection(e.target.value)}
            aria-label="Describe the correct answer"
            placeholder="Optional: describe the correct answer or the right approach..."
            rows={2}
            style={{
              width: "100%",
              boxSizing: "border-box",
              border: "1px solid var(--color-border-secondary)",
              borderRadius: 6,
              padding: "8px 10px",
              fontSize: 12,
              lineHeight: "18px",
              fontFamily: "inherit",
              resize: "vertical",
              color: "var(--color-text-primary)",
              background: "var(--color-bg-primary)",
              outline: "none",
            }}
          />

          <div style={{ display: "flex", gap: 8, marginTop: 8, alignItems: "center" }}>
            <button
              onClick={handleSubmitFeedback}
              disabled={submitting}
              style={{
                height: 28,
                padding: "0 12px",
                borderRadius: 6,
                border: "none",
                background: "var(--color-brand-900)",
                color: "var(--color-bg-primary)",
                fontSize: 12,
                fontWeight: 500,
                cursor: submitting ? "wait" : "pointer",
                fontFamily: "inherit",
                opacity: submitting ? 0.7 : 1,
              }}
            >
              {submitting ? "Submitting…" : "Submit"}
            </button>
            <button
              onClick={() => {
                setFeedbackOpen(false);
                setRating(null);
              }}
              style={{
                height: 28,
                padding: "0 10px",
                borderRadius: 6,
                border: "1px solid var(--color-border-secondary)",
                background: "var(--color-bg-primary)",
                color: "var(--color-text-secondary)",
                fontSize: 12,
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              Cancel
            </button>
            <span style={{ fontSize: 11, color: "var(--color-text-quaternary)", marginLeft: 4 }}>
              Corrections are reviewed to improve metric definitions
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ExploreNext — clickable follow-up suggestions shown at the end of an answer
// ---------------------------------------------------------------------------
function ExploreNext({
  suggestions,
  onExplore,
  disabled,
}: {
  suggestions: Suggestion[];
  onExplore?: (question: string) => void;
  disabled?: boolean;
}) {
  const drawer = useAskSurface() === "drawer";
  if (!suggestions || suggestions.length === 0) return null;

  if (drawer) {
    return (
      <div style={{ marginTop: 14 }}>
        <SectionLabel label="Explore next" />
        <div className="ask-explore-list">
          {suggestions.map((s) => (
            <button
              key={s.question}
              type="button"
              onClick={() => onExplore?.(s.question)}
              disabled={disabled}
              title={s.kind === "deeper" ? "Go deeper" : "Broaden out"}
              className="ask-explore-row"
            >
              <Icon
                name={s.kind === "deeper" ? "search" : "layers"}
                size={15}
                color="currentColor"
                strokeWidth={1.75}
              />
              <span className="ask-explore-question">{s.question}</span>
              <span className="ask-explore-arrow">
                <Icon name="arrowUpRight" size={14} color="currentColor" strokeWidth={2} />
              </span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div style={{ marginTop: 14 }}>
      <SectionLabel label="Explore next" />

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {suggestions.map((s, i) => (
          <button
            key={i}
            onClick={() => {
              if (onExplore) onExplore(s.question);
            }}
            disabled={disabled}
            title={s.kind === "deeper" ? "Go deeper" : "Broaden out"}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              width: "100%",
              textAlign: "left",
              padding: "11px 14px",
              borderRadius: 12,
              border: "1px solid var(--color-border-secondary)",
              background: "var(--color-bg-primary)",
              color: "var(--color-text-secondary)",
              fontSize: 14,
              fontWeight: 500,
              lineHeight: "21px",
              fontFamily: "inherit",
              cursor: disabled ? "not-allowed" : "pointer",
              opacity: disabled ? 0.5 : 1,
              transition: "background 120ms, border-color 120ms",
            }}
            onMouseEnter={(e) => {
              if (disabled) return;
              e.currentTarget.style.background = "var(--color-brand-50)";
              e.currentTarget.style.borderColor = "var(--color-brand-300)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "var(--color-bg-primary)";
              e.currentTarget.style.borderColor = "var(--color-border-secondary)";
            }}
          >
            <span style={{ flexShrink: 0, display: "inline-flex", color: "var(--color-brand-600)" }}>
              <Icon
                name={s.kind === "deeper" ? "search" : "layers"}
                size={15}
                color="currentColor"
                strokeWidth={1.75}
              />
            </span>
            <span style={{ flex: 1, minWidth: 0 }}>{s.question}</span>
            <span style={{ flexShrink: 0, display: "inline-flex", color: "var(--color-text-quaternary)" }}>
              <Icon name="arrowUpRight" size={14} color="currentColor" strokeWidth={2} />
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// MethodologyDetails — collapsible card consolidating notes, metadata, SQL,
// and the disclaimer. Replaces the old scattered notes/footer/SQL blocks.
// ---------------------------------------------------------------------------
function MethodologyDetails({ response, termMap }: { response: QueryResponse; termMap: Map<string, GlossaryTerm> }) {
  const [open, setOpen] = useState(false);
  const [sqlOpen, setSqlOpen] = useState(false);

  const hasNotes = !!response.notes;
  const hasWindow = !!response.window;
  const hasTables = (response.tables_referenced?.length ?? 0) > 0;
  const hasDefs = (response.definitions_applied?.length ?? 0) > 0;
  const hasSql = !!response.sql;
  const hasContent = showQueryInternals && (hasNotes || hasWindow || hasTables || hasDefs || hasSql);

  const disclaimer = (
    <div style={{ fontSize: 11, color: "var(--color-text-quaternary)" }}>
      Answers reflect live warehouse data. Verify before sharing externally.
    </div>
  );

  // No methodology content → just the disclaimer, no card chrome
  if (!hasContent) {
    return <div style={{ marginTop: 12 }}>{disclaimer}</div>;
  }

  return (
    <div
      style={{
        marginTop: 14,
        border: "1px solid var(--color-border-secondary)",
        borderRadius: 12,
        overflow: "hidden",
      }}
    >
      <button
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "10px 14px",
          background: "var(--color-bg-primary)",
          border: "none",
          cursor: "pointer",
          fontFamily: "inherit",
          textAlign: "left",
        }}
      >
        <Icon
          name={open ? "chevronDown" : "chevronRight"}
          size={14}
          color="var(--color-text-quaternary)"
          strokeWidth={2}
        />
        <span style={{ fontSize: 13, fontWeight: 600, color: "var(--color-text-primary)" }}>
          Methodology &amp; definitions
        </span>
        <span style={{ fontSize: 11, color: "var(--color-text-quaternary)" }}>View SQL · definitions · window</span>
      </button>

      {open && (
        <div style={{ padding: "0 14px 14px", display: "flex", flexDirection: "column", gap: 12 }}>
          {hasNotes && (
            <div
              style={{
                display: "flex",
                gap: 8,
                background: "var(--color-brand-50)",
                border: "1px solid var(--color-brand-200)",
                borderRadius: 8,
                padding: "10px 12px",
              }}
            >
              <span style={{ flexShrink: 0, color: "var(--color-brand-700)", marginTop: 1 }}>
                <Icon name="info" size={14} color="currentColor" strokeWidth={2} />
              </span>
              <div style={{ color: "var(--color-brand-900)", flex: 1, minWidth: 0 }}>
                <Markdown text={response.notes!} termMap={termMap} style={{ fontSize: 12, lineHeight: "18px" }} />
              </div>
            </div>
          )}

          {(hasWindow || hasTables || hasDefs) && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 4,
                fontSize: 11,
                fontFamily: "var(--font-mono)",
                color: "var(--color-text-secondary)",
              }}
            >
              {hasWindow && <div>Window: {response.window}</div>}
              {hasTables && <div>Source tables: {response.tables_referenced.join(", ")}</div>}
              {hasDefs && <div>Definitions: {response.definitions_applied.join(", ")}</div>}
            </div>
          )}

          {hasSql && (
            <div>
              <button
                onClick={() => setSqlOpen((v) => !v)}
                aria-expanded={sqlOpen}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 5,
                  fontSize: 12,
                  fontWeight: 500,
                  color: "var(--color-brand-700)",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  fontFamily: "inherit",
                  padding: 0,
                }}
              >
                <Icon name={sqlOpen ? "chevronDown" : "chevronRight"} size={12} color="currentColor" strokeWidth={2} />
                {sqlOpen ? "Hide SQL" : "View SQL"}
              </button>
              {sqlOpen && (
                <pre
                  style={{
                    marginTop: 8,
                    background: "var(--color-gray-950)",
                    border: "1px solid var(--color-gray-800)",
                    borderRadius: 8,
                    padding: "12px 14px",
                    fontSize: 12,
                    lineHeight: "18px",
                    color: "rgb(134,239,172)",
                    overflowX: "auto",
                    fontFamily: "var(--font-mono)",
                    whiteSpace: "pre",
                  }}
                >
                  {response.sql}
                </pre>
              )}
            </div>
          )}

          {disclaimer}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// UserMessage
// ---------------------------------------------------------------------------
export function UserMessage({ text, scope: _scope }: { text: string; scope?: Scope | null }) {
  if (useAskSurface() === "drawer") {
    return (
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <div className="ask-glass-bubble">{text}</div>
      </div>
    );
  }
  return (
    <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "flex-start", gap: 12 }}>
      <div
        style={{
          maxWidth: 600,
          background: "var(--color-brand-900)",
          color: "var(--color-text-primary_on-brand)",
          borderRadius: 14,
          borderTopRightRadius: 4,
          padding: "10px 16px",
          fontSize: 15,
          lineHeight: "22px",
          wordBreak: "break-word",
        }}
      >
        {text}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ThinkingIndicator
// ---------------------------------------------------------------------------
export function ThinkingIndicator() {
  return (
    <div
      role="status"
      aria-label="Thinking…"
      style={{ display: "flex", gap: 5, alignItems: "center", padding: "4px 0" }}
    >
      {[0, 150, 300].map((delay) => (
        <span
          key={delay}
          style={{
            width: 7,
            height: 7,
            borderRadius: "50%",
            background: "var(--color-gray-300)",
            display: "inline-block",
            animation: `bounce 1.2s ${delay}ms ease-in-out infinite`,
          }}
        />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ElapsedTimer — running m:ss counter, monotonic across step changes
// ---------------------------------------------------------------------------
function ElapsedTimer({ startRef }: { startRef: React.MutableRefObject<number> }) {
  const [, force] = useState(0);
  useEffect(() => {
    const id = setInterval(() => force((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);
  const secs = Math.floor((Date.now() - startRef.current) / 1000);
  const label = `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, "0")}`;
  return (
    <span
      style={{
        marginLeft: "auto",
        fontSize: 11,
        color: "var(--color-text-quaternary)",
        fontFamily: "var(--font-mono)",
      }}
    >
      {label}
    </span>
  );
}

function SourcesBlock({ steps, currentStepLabel }: { steps: AgentStep[]; currentStepLabel?: string }) {
  const [openStep, setOpenStep] = useState<number | null>(null);
  const [stepSqlOpen, setStepSqlOpen] = useState<Record<number, boolean>>({});
  // Start time held in a ref so it doesn't reset when currentStepLabel changes
  const runStartRef = useRef(Date.now());

  const toggleStepSql = (step: number) => setStepSqlOpen((prev) => ({ ...prev, [step]: !prev[step] }));

  const active = openStep != null ? (steps.find((s) => s.step === openStep) ?? null) : null;

  return (
    <div style={{ marginBottom: 14 }}>
      <SectionLabel label="Sources" />

      {/* Pill row */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
        {steps.map((step) => {
          const isOpen = openStep === step.step;
          return (
            <button
              key={step.step}
              onClick={() => setOpenStep(isOpen ? null : step.step)}
              aria-expanded={isOpen}
              aria-label={`Source ${step.step}: ${step.label}`}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "5px 10px",
                borderRadius: 99,
                border: `1px solid ${isOpen ? "var(--color-brand-300)" : "var(--color-border-secondary)"}`,
                background: "var(--color-bg-primary)",
                cursor: "pointer",
                fontFamily: "inherit",
                transition: "border-color 120ms",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "var(--color-brand-300)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = isOpen ? "var(--color-brand-300)" : "var(--color-border-secondary)";
              }}
            >
              <span
                style={{
                  width: 18,
                  height: 18,
                  borderRadius: "50%",
                  background: step.error ? "var(--color-error-700)" : "var(--color-brand-600)",
                  color: "var(--color-bg-primary)",
                  fontSize: 10,
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                {step.step}
              </span>
              <span style={{ fontSize: 13, fontWeight: 500, color: "var(--color-text-secondary)" }}>{step.label}</span>
              {step.error ? (
                <span style={{ fontSize: 11, color: "var(--color-error-700)" }}>error</span>
              ) : (
                showQueryInternals && (
                  <span
                    style={{
                      fontSize: 11,
                      color: "var(--color-text-quaternary)",
                      fontFamily: "var(--font-mono)",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {step.row_count} rows · {step.execution_ms}ms
                  </span>
                )
              )}
            </button>
          );
        })}

        {/* In-progress pill (streaming only) */}
        {currentStepLabel && (
          <span
            role="status"
            aria-label={`Running: ${currentStepLabel}`}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "5px 10px",
              borderRadius: 99,
              border: "1px solid var(--color-border-secondary)",
              background: "var(--color-gray-50)",
            }}
          >
            <span
              style={{
                width: 16,
                height: 16,
                borderRadius: "50%",
                border: "2px solid var(--color-brand-600)",
                borderTopColor: "transparent",
                animation: "spin 0.8s linear infinite",
                flexShrink: 0,
              }}
            />
            <span style={{ fontSize: 13, fontWeight: 500, color: "var(--color-text-secondary)" }}>
              {currentStepLabel}
            </span>
          </span>
        )}

        {currentStepLabel && <ElapsedTimer startRef={runStartRef} />}
      </div>

      {/* Detail panel for the open pill */}
      {active && (
        <div
          style={{
            marginTop: 10,
            border: "1px solid var(--color-border-secondary)",
            borderRadius: 8,
            padding: "12px",
            fontSize: 13,
          }}
        >
          {active.error ? (
            <div style={{ fontSize: 12, color: "var(--color-error-700)" }}>{active.error}</div>
          ) : (
            <ResultsTable
              columns={active.columns}
              rows={active.rows}
              row_count={active.row_count}
              execution_ms={active.execution_ms}
              forceTable
            />
          )}
          {showQueryInternals && active.sql && (
            <div style={{ marginTop: 8 }}>
              <button
                onClick={() => toggleStepSql(active.step)}
                aria-expanded={!!stepSqlOpen[active.step]}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 5,
                  fontSize: 11,
                  fontWeight: 500,
                  color: "var(--color-text-quaternary)",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  fontFamily: "inherit",
                  padding: 0,
                }}
              >
                <Icon name="chevronRight" size={11} color="currentColor" strokeWidth={2} />
                {stepSqlOpen[active.step] ? "Hide SQL" : "View SQL"}
              </button>
              {stepSqlOpen[active.step] && (
                <pre
                  style={{
                    marginTop: 6,
                    background: "var(--color-gray-950)",
                    border: "1px solid var(--color-gray-800)",
                    borderRadius: 8,
                    padding: "12px 14px",
                    fontSize: 12,
                    lineHeight: "18px",
                    color: "rgb(134,239,172)",
                    overflowX: "auto",
                    fontFamily: "var(--font-mono)",
                    whiteSpace: "pre",
                  }}
                >
                  {active.sql}
                </pre>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ErrorActionButton — small ghost button for error recovery actions
// ---------------------------------------------------------------------------
function ErrorActionButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: "inline-flex",
        alignItems: "center",
        height: 26,
        padding: "0 10px",
        borderRadius: 6,
        border: "1px solid var(--color-border-primary)",
        background: "var(--color-bg-primary)",
        color: "var(--color-text-secondary)",
        fontSize: 12,
        fontWeight: 500,
        cursor: "pointer",
        fontFamily: "inherit",
        transition: "background 80ms, border-color 80ms",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = "var(--color-gray-50)";
        e.currentTarget.style.borderColor = "var(--color-border-secondary)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "var(--color-bg-primary)";
        e.currentTarget.style.borderColor = "var(--color-border-primary)";
      }}
    >
      {label}
    </button>
  );
}

// ---------------------------------------------------------------------------
// ErrorBlock — styled error box for timeout / warehouse / generic errors
// ---------------------------------------------------------------------------
function ErrorBlock({
  err,
  raw,
  question,
  onExplore,
  onRephrase,
}: {
  err: import("@/pages/ask/lib/error-display").CategorizedError;
  raw: string;
  question?: string;
  onExplore?: (question: string) => void;
  onRephrase?: (question: string) => void;
}) {
  const [detailsOpen, setDetailsOpen] = useState(false);

  return (
    <div
      style={{
        background: "var(--color-error-50)",
        border: "1px solid rgb(254,205,202)",
        borderRadius: 8,
        padding: "10px 14px",
        fontSize: 13,
        marginBottom: 12,
      }}
    >
      {/* Title */}
      <div style={{ fontWeight: 600, color: "var(--color-error-700)", marginBottom: 4 }}>{err.title}</div>
      {/* Humanized message */}
      <div style={{ color: "var(--color-error-700)", lineHeight: "19px", marginBottom: 10 }}>{err.message}</div>

      {/* Action row */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {question && <ErrorActionButton label="Try again" onClick={() => onExplore?.(question)} />}
        {question && <ErrorActionButton label="Rephrase" onClick={() => onRephrase?.(question)} />}
      </div>

      {/* Details toggle — raw error string for engineers */}
      <div style={{ marginTop: 8 }}>
        <button
          onClick={() => setDetailsOpen((v) => !v)}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            fontSize: 11,
            color: "var(--color-error-600)",
            background: "none",
            border: "none",
            cursor: "pointer",
            fontFamily: "inherit",
            padding: 0,
            opacity: 0.7,
          }}
        >
          <Icon name={detailsOpen ? "chevronDown" : "chevronRight"} size={10} color="currentColor" strokeWidth={2} />
          {detailsOpen ? "Hide details" : "Details"}
        </button>
        {detailsOpen && (
          <div
            style={{
              marginTop: 4,
              fontSize: 11,
              fontFamily: "var(--font-mono)",
              color: "var(--color-error-700)",
              wordBreak: "break-all",
              lineHeight: "16px",
              opacity: 0.8,
            }}
          >
            {raw}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// AssistantMessage
// ---------------------------------------------------------------------------
export function AssistantMessage({
  response,
  generatedReport,
  thinking = false,
  streamingText,
  agentSteps,
  currentStepLabel,
  streamingFindings,
  streamingPhase,
  streamingDisplay,
  reportRun,
  stopped,
  question,
  sessionId,
  messageId,
  onExplore,
  onRephrase,
  disabled,
}: {
  response?: QueryResponse;
  generatedReport?: GenerateResponse;
  thinking?: boolean;
  streamingText?: string;
  agentSteps?: AgentStep[];
  currentStepLabel?: string;
  streamingFindings?: string[];
  streamingPhase?: StreamingPhase;
  streamingDisplay?: Visualization | null;
  reportRun?: boolean;
  stopped?: boolean;
  question?: string;
  sessionId?: string;
  messageId?: string;
  onExplore?: (question: string) => void;
  onRephrase?: (question: string) => void;
  disabled?: boolean;
}) {
  // The single answer table to export: the model-designated query result, else
  // the lone single-query result. Null when the answer isn't backed by a table.
  const answerTable: { columns: string[]; rows: Record<string, unknown>[] } | null = (() => {
    if (!response) return null;
    const idx = response.answer_query_index;
    if (idx != null) {
      const step = response.steps?.find((s) => s.step === idx);
      if (step && !step.error && step.rows.length > 0) return { columns: step.columns, rows: step.rows };
      return null;
    }
    if ((response.steps?.length ?? 0) === 0 && response.results && response.results.length > 0) {
      return { columns: response.columns, rows: response.results };
    }
    return null;
  })();

  function handleExportCsv() {
    if (!answerTable) return;
    const csv = toCsv(answerTable.columns, answerTable.rows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ask-unbox-export-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  // Glossary terms for inline annotation — filtered to only the definitions
  // Claude applied in this response so we don't over-annotate.
  const allTerms = useGlossaryTerms();
  const termMap = useMemo(() => {
    if (!response?.definitions_applied?.length || allTerms.size === 0) {
      return new Map<string, GlossaryTerm>();
    }
    const applied = new Set(response.definitions_applied.map((d) => d.toLowerCase()));
    const filtered = new Map<string, GlossaryTerm>();
    for (const [key, term] of allTerms) {
      if (applied.has(term.term.toLowerCase())) {
        filtered.set(key, term);
      }
    }
    return filtered;
  }, [allTerms, response?.definitions_applied]);

  const linkEntities = useMemo(() => collectLinkEntities(response?.steps ?? []), [response?.steps]);
  const streamEntities = useMemo(() => collectLinkEntities(agentSteps ?? []), [agentSteps]);
  const drawer = useAskSurface() === "drawer";
  const { label: status, skeleton } = useAnswerStatus({
    streaming:
      !stopped &&
      !response &&
      !generatedReport &&
      (thinking || agentSteps !== undefined || !!currentStepLabel || !!streamingText),
    agentSteps,
    currentStepLabel,
    streamingText,
    streamingPhase,
    streamingDisplay,
    reportRun,
    stopped,
  });

  return (
    <div style={{ display: "flex", justifyContent: "flex-start", alignItems: "flex-start", gap: 12 }}>
      <AssistantAvatar />

      <div style={{ flex: 1, minWidth: 0 }}>
        {thinking && !agentSteps?.length && !currentStepLabel ? (
          stopped ? (
            <div style={{ marginTop: 10, fontSize: 12, color: "var(--color-text-quaternary)", fontStyle: "italic" }}>
              Stopped
            </div>
          ) : drawer ? (
            <AnswerStatus label={status} />
          ) : (
            <ThinkingIndicator />
          )
        ) : agentSteps !== undefined || currentStepLabel ? (
          <>
            <AnswerSources steps={agentSteps ?? []} currentStepLabel={currentStepLabel} />
            {streamingText && <AnswerText text={streamingText} entities={streamEntities} />}
            {/* The display renders from the step rows already streamed, before the answer finishes */}
            {streamingDisplay && agentSteps && (
              <div className="ask-enter">
                <Visualizations response={{ steps: agentSteps, visualizations: [streamingDisplay] }} />
              </div>
            )}
            {skeleton && (
              <div className="ask-enter">
                <DisplaySkeleton kind={skeleton} />
              </div>
            )}
            {streamingFindings && streamingFindings.length > 0 && (
              <div className="ask-enter" style={{ marginTop: 12 }}>
                <AnswerFindings findings={streamingFindings} entities={streamEntities} />
              </div>
            )}
            {/* The status sits below what has arrived, so nothing above it moves when it goes */}
            <AnswerStatus label={status} />
            {stopped && (
              <div style={{ marginTop: 10, fontSize: 12, color: "var(--color-text-quaternary)", fontStyle: "italic" }}>
                Stopped
              </div>
            )}
          </>
        ) : streamingText ? (
          <>
            <Markdown
              text={streamingText}
              style={{
                fontSize: 15,
                lineHeight: "24px",
                color: "var(--color-text-primary)",
              }}
            />
            {stopped && (
              <div style={{ marginTop: 10, fontSize: 12, color: "var(--color-text-quaternary)", fontStyle: "italic" }}>
                Stopped
              </div>
            )}
          </>
        ) : generatedReport ? (
          <>
            <ReportViewer report={generatedReport} />
            <ExploreNext
              suggestions={REPORT_FOLLOWUPS[generatedReport.templateId] ?? []}
              onExplore={onExplore}
              disabled={disabled}
            />
          </>
        ) : response ? (
          <>
            {/* Sources — they lead the answer on the page; the drawer lists them at the end */}
            {!drawer && response.steps && response.steps.length > 0 && <AnswerSources steps={response.steps} />}

            {response.explanation && (
              <AnswerText
                text={response.explanation}
                termMap={termMap}
                entities={linkEntities}
                labelled={response.steps.length > 0}
                errored={!!response.error}
              />
            )}

            {/* Charts — model-directed, row-referenced; renders nothing if none/invalid */}
            <Visualizations response={response} />

            {response.findings && response.findings.length > 0 && (
              <AnswerFindings findings={response.findings} termMap={termMap} entities={linkEntities} />
            )}

            {/* Error — categorized with actionable recovery */}
            {response.error && (
              <AnswerError
                err={categorizeError(response.error)}
                raw={response.error}
                question={question}
                onExplore={onExplore}
                onRephrase={onRephrase}
              />
            )}

            {/* Results table — single-query path (no steps): the answer's data */}
            {response.results !== null && response.columns && response.steps.length === 0 && (
              <ResultsTable
                columns={response.columns}
                rows={response.results ?? []}
                row_count={response.row_count}
                execution_ms={response.execution_ms}
              />
            )}

            {/* Methodology & definitions — notes, window, tables, defs, SQL, disclaimer */}
            <MethodologyDetails response={response} termMap={termMap} />

            {/* Actions — export CSV + feedback */}
            <ResponseActions
              response={response}
              question={question}
              sessionId={sessionId}
              messageId={messageId}
              answerTable={answerTable}
              onExportCsv={handleExportCsv}
            />

            {/* Suggested follow-up explorations — never under an errored answer */}
            {!response.error && (
              <div style={drawer ? { marginTop: 32 } : undefined}>
                <ExploreNext
                  suggestions={response.suggested_explorations ?? []}
                  onExplore={onExplore}
                  disabled={disabled}
                />
              </div>
            )}

            {drawer && response.steps && response.steps.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <AnswerSources steps={response.steps} />
              </div>
            )}
          </>
        ) : null}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// MessageBubble — dispatch to the right sub-component
// ---------------------------------------------------------------------------
function MessageBubble({
  message,
  onExplore,
  onRephrase,
  disabled,
}: {
  message: Message;
  onExplore?: (question: string) => void;
  onRephrase?: (question: string) => void;
  disabled?: boolean;
}) {
  if (message.role === "user") {
    return <UserMessage text={message.content} scope={message.scope} />;
  }
  // Show the forming explanation whenever we have streamed text but no final
  // response yet — including while completed step chips are on screen.
  const streamingText =
    !message.queryResponse && !message.generatedReport && message.content ? message.content : undefined;
  return (
    <AssistantMessage
      response={message.queryResponse}
      generatedReport={message.generatedReport}
      thinking={
        !message.queryResponse &&
        !message.content &&
        !message.generatedReport &&
        !message.agentSteps &&
        !message.currentStepLabel
      }
      streamingText={streamingText}
      agentSteps={message.agentSteps}
      currentStepLabel={message.currentStepLabel}
      streamingFindings={message.streamingFindings}
      streamingPhase={message.streamingPhase}
      streamingDisplay={message.streamingDisplay}
      reportRun={message.reportRun}
      stopped={message.stopped}
      question={message.question}
      sessionId={message.sessionId}
      messageId={message.messageId}
      onExplore={onExplore}
      onRephrase={onRephrase}
      disabled={disabled}
    />
  );
}

export default memo(MessageBubble);
